import '../../components/animations';
import '../../components/promo';
import '../../sections/contacts';
import '../../sections/content-block';
import '../../sections/infrastructure';
import '../../sections/first-screen';
import type * as ClassTypes from './types/class';
import {
    initCallbackPopup,
    initForm,
    initHeader,
    initImagePoints,
    initImageSlider,
    initLocation,
    initPopup,
    initTabs,
    lazyLoadCounters,
    lazyLoadElements
} from './common';
import type {IDynamicImport} from './types/utils';
import initDetailFlat from '@/sections/detail-flat/detail-flat';
import {initElector} from '@/sections/elector/elector';
import initFlatSlider from '@/sections/flat-slider/flat-slider';

const setVH = (): void => {
    // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
    let vh = window.innerHeight * 0.01;

    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`);

    // We listen to the resize event
    window.addEventListener('resize', () => {
    // We execute the same script as before
        vh = window.innerHeight * 0.01;

        document.documentElement.style.setProperty('--vh', `${vh}px`);
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        setVH();
    });
} else {
    setVH();
}


// Поведение шапки
const headerWrap = document.querySelector('.j-header') as HTMLElement;

if (headerWrap) {
    initHeader(headerWrap);
}

const imageSliders = document.querySelectorAll<HTMLElement>('.j-slider-images');

if (imageSliders.length) {
    imageSliders.forEach((sliderWrap: HTMLElement) => {
        initImageSlider(sliderWrap);
    });
}

const newsSliders = document.querySelectorAll<HTMLElement>('.j-slider-news');

if (newsSliders.length) {
    import(/* webpackChunkName: "initNewsSlider" */ '@/sections/news')
        .then(({default: initNewsSlider}: IDynamicImport) => {
            newsSliders.forEach((sliderWrap: HTMLElement) => {
                initNewsSlider(sliderWrap);
            });
        });
}

const progressSliders = document.querySelectorAll<HTMLElement>('.j-slider-progress');

if (progressSliders.length) {
    import(/* webpackChunkName: "initProgressSlider" */ '@/sections/progress-detail')
        .then(({default: initProgressSlider}: IDynamicImport) => {
            progressSliders.forEach((sliderWrap: HTMLElement) => {
                initProgressSlider(sliderWrap);
            });
        });
}

const historyLocation = document.querySelector<HTMLElement>('.j-location-map');

if (historyLocation) {
    initLocation(historyLocation);
}

// Details Accordion
const details = [...document.querySelectorAll('.j-details')];

if (details.length) {
    import(/* webpackChunkName: "details-accordion" */ '@/components/details')
        .then(({default: DetailsAccordion}: IDynamicImport) => {
            details.forEach((element: Element) => {
                const accordion: ClassTypes.IDetailsAccordion = new DetailsAccordion();

                accordion.init(element as HTMLDetailsElement);
            });
        });
}

/* Ленивая загрузка */
/* подключать после инициализации слайдеров */
const lazyElements = [...document.querySelectorAll('[loading=lazy]')];

if (lazyElements.length) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            lazyLoadElements(lazyElements);
        });
    } else {
        lazyLoadElements(lazyElements);
    }
}

// Плавный подскролл по якорям
const simpleAnchors = [...document.querySelectorAll(`a[href^="#"]:not(.j-anchor)`)];

if (simpleAnchors.length) {
    simpleAnchors.forEach((anchor: Element) => {
        const anchorHref = anchor.getAttribute('href');

        if (!anchorHref || anchorHref?.length === 1) {
            return;
        }

        const anchorBlock = document.querySelector(anchorHref);

        if (anchorBlock) {
            (anchor as HTMLElement).addEventListener('click', (event: MouseEvent) => {
                event.preventDefault();

                anchorBlock.scrollIntoView({
                    behavior: 'smooth'
                });
            });
        }
    });
}

const lazyCounters = [...document.querySelectorAll<HTMLElement>('.j-lazy-counter')];

if (lazyElements.length) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            lazyLoadCounters(lazyCounters);
        });
    } else {
        lazyLoadCounters(lazyCounters);
    }
}

// Формы
const formWrappers = [...document.querySelectorAll('.j-form')];

formWrappers.forEach((wrapper: Element) => {
    initForm(wrapper as HTMLElement);
});

// Попапы
const popupSelectors = [...document.querySelectorAll('.j-popup')] as HTMLElement[];

if (popupSelectors.length) {
    initPopup(popupSelectors, {removeDelay: 300});
}

// Отдельный селектор для попапов обратных звонков
const popupFacadeLinks = [...document.querySelectorAll('.j-popup-callback:not([data-init])')] as HTMLElement[];

if (popupFacadeLinks.length) {
    initCallbackPopup(popupFacadeLinks);

    popupFacadeLinks.forEach((button: HTMLElement) => {
        button.dataset['init'] = 'true';
    });
}

const imagePoints = [...document.querySelectorAll<HTMLElement>('.j-image-points')];

imagePoints.forEach((wrapper: HTMLElement) => {
    initImagePoints(wrapper);
});

// Уведомление об использовании cookies
const cookiesWrapper = document.querySelector('.j-cookies-notice') as HTMLElement;

if (cookiesWrapper) {
    import(/* webpackChunkName: "cookies-notice" */ '@/components/cookies-notice')
        .then(({default: CookiesNotice}: IDynamicImport) => {
            const cookiesNotice: ClassTypes.ICookiesNotice = new CookiesNotice();

            cookiesNotice.init(cookiesWrapper);
        });
}

const tourWrapper = document.querySelector<HTMLElement>('.j-tour');

if (tourWrapper) {
    import(/* webpackChunkName: "tour" */ '@/sections/tour')
        .then(({default: Tour}: IDynamicImport) => {
            new Tour().init(tourWrapper);
        });
}

// Табы
const tabs = [...document.querySelectorAll('.j-tabs-wrap')];

tabs.forEach((elem: Element) => {
    initTabs(elem as HTMLElement);
});

// Подборщик
const electorBlockEl = document.querySelector('.elector');

if (electorBlockEl) {
    initElector(electorBlockEl as HTMLElement);
}

// Детальная квартиры
const blockDetailFlatEl = document.querySelector('.detail-flat');

if (blockDetailFlatEl) {
    initDetailFlat(blockDetailFlatEl as HTMLElement);
}

// Слайдер с карточками квартиры
const blockFlatSlider = document.querySelector('.flat-slider');

if (blockFlatSlider) {
    initFlatSlider(blockFlatSlider as HTMLElement);
}
